import { Injectable, Injector } from '@angular/core';
import { Account } from './account.service';
import { Config } from '../config';
import { Observable, Observer } from 'rxjs';
import { HttpService } from './http.service';
import { OAuthToken } from '../models/oauth-token';
import { OAuthTokenRequest } from '../models/oauth-token-request';
import { UserRoles } from '../models/user_roles';
import { IdPUser } from '../models/idpuser';
import { IdPUserReply } from '../models/idpusers_reply';
import { FindIdPUser } from '../models/find_idpuser';
import { IdPUserTextSearch } from '../models/idpuser_text_search';
import { IdPUserTextSearchCount } from '../models/idpuser_text_search_count';
import { IdPUserRequest } from '../models/idpuser_request';
import { IdPUserRoleReply } from '../models/idpuser_role_reply';
import { IdPUserRole } from '../models/idpuser_role';
import { IdP } from '../models/idp';
import { IdPReply } from '../models/idp_reply';
import { IdPRequest } from '../models/idp_request';
import { IdentityUser } from '../models/identity_user';
import { InviteUser } from '../models/invite_user';
import { FindIdP } from '../models/find_idp';
import { SAML2Config } from '../models/saml2_config';
import { SAML2Attribute } from '../models/saml2_attribute';
import { CountAccountAssociation } from '../models/count_account_association';
import { FindAccountAssociation } from '../models/find_account_association';
import { AccountAssociation } from '../models/account_association';
import { IdPDomain } from '../models/idp_domain';
import { IdPDomainReply } from '../models/idp_domain_reply';
import { IdPDomainRequest } from '../models/idp_domain_request';
import { EndpointsReply } from '../models/test_urls_reply';
import { IdPRolesMapping } from '../models/idp_roles_mapping';
import { IdPRolesMappingReply } from '../models/idp_roles_mapping_reply';
import { IdPRolesMappingRequest } from '../models/idp_roles_mapping_request';
import { IdPRolesMappingTextSearch } from '../models/idp_roles_mapping_text_search';
import { IdPRolesMappingTextSearchCount } from '../models/idp_roles_mapping_search_count';

@Injectable()
export class IdentityServiceMoc {
	userRolesKey: string = 'userRoles';
	timeout: number = 5000; //10 sec
	retry: number = 3; //max retry
	//delay:number = 10000; //max delay after retry
	errors: any = {};
	maxPollIteration: number = 5;

	constructor(private http: HttpService, private account: Account, private config: Config) {
		this.errors['default'] = 'There is an error when processing this request, please try again later.';
		this.errors['no connection'] = 'There is an error when communicating with a server, please try again later.'
		this.errors['unauthorized'] = 'Unauthorized access.';
		this.errors['not found'] = 'No results.';
		this.errors['invalid token'] = 'Unauthorized access.';
		this.errors['duplicate entry'] = 'This data already exists.';
		this.errors['bad request'] = 'Invalid request.';
		this.errors['internal server error'] = 'Internal server error. Please try again later.';
		this.errors['unexpected'] = 'Sorry, an unexpected error has occured. Please contact Tech Support.';
		this.errors['timeout'] = 'Timeout when communicating with a server, please try again later.';
		this.errors['email address is invalid'] = 'Email address is invalid.';
		this.errors['user status is invalid'] = 'Invalid user status.';
		this.errors['user status signup is invalid email not confirmed'] = 'The user you\'ve invited has not yet confirmed their email address.';
		this.errors['notification not supported'] = 'Push notifications are not supported in this browser.';
		this.errors['account license expired'] = 'Account License has expired. Please visit the Subscription page for more details and license renewal.';
		this.errors['invalid account license'] = 'Account License is invalid. Please visit the Subscription page for more details and license renewal.';
		this.errors['unauthorized access role'] = 'Unauthorized access role.';
	}

	validateAcctNumber(acctNumber: string): Observable<boolean> {
		let lusr = this.userRoles();
		if (lusr != null && lusr.acctNumber == acctNumber) {
			//return non http observable when the userroles exists at localStorage
			return Observable.create((observer: Observer<boolean>) => {
				observer.next(true);
				observer.complete();
			});
		}
		return Observable.create((observer: Observer<boolean>) => {
			observer.next(false);
			observer.complete();
		});
	}

	loadUser(): Observable<UserRoles> {
		let u = this.account.getUserProfileApi().subscribe(us => {
			this.account.setUser(us);
		});
		return this.userRolesApi().map(urs => {
			this.setUserRoles(urs[0]);
			return urs[0]
		}).do(ur => u);
	}

	loadUserByAcctNumber(acctNumber: string): Observable<UserRoles> {
		let u = this.account.getUserProfileApi().subscribe(us => {
			this.account.setUser(us);
		});
		return this.userRolesApi().map(urs => {
			for (let a of urs) {
				if (a.acctNumber === acctNumber) {
					this.setUserRoles(a);
					return a;
				}
			}
			throw new Error('unauthorized');
		})
		.do(ur => u);
	}


	removeLocalStorage() {
		localStorage.removeItem(this.userRolesKey);
	}

	setUserRoles(us: UserRoles) {
		localStorage.setItem(this.userRolesKey, JSON.stringify(us));
	}

	userRoles() {
		if (localStorage.getItem(this.userRolesKey) === null) {
			return null
		}
		return JSON.parse(localStorage.getItem(this.userRolesKey));
	}

	getToken() {
		return this.account.getToken();
	}

	getError(err: any): Error {
		var er: any;
		var msg = 'no connection';
		if (typeof (err.json) === 'function') {
			er = err.json();
			if (er.message != null && er.message != null && er.message != '') {
				msg = er.message;
			} else {
				console.log(err);
			}
			return new Error(msg);
		} else if (err instanceof Error) {
			return err;
		} else {
			console.log(err);
		}
	}

	getUserError(err: Error): string {
		return this.errors[err.message];
	}

	isErrNotFound(err: Error): boolean {
		return err.message == 'not found';
	}

	isErrUnauthorized(err: Error): boolean {
		return err.message == 'unauthorized' ||
			err.message == 'unauthorized access role';
	}

	getUserErrorDefault(err: Error): string {
		let e = this.errors[err.message];
		if (e != null) {
			return e;
		}
		return this.errors['default'];
	}

	buildUrl(parameters) {
		var qs = "";
		for (var key in parameters) {
			var value = parameters[key];
			if (value != null && value != '') {
				qs += encodeURIComponent(key) + "=" + encodeURIComponent(value) + "&";
			}
		}
		if (qs.length > 0) {
			return qs.substring(0, qs.length - 1); //chop off last "&"
		}
		return '';
	}

	params(parameters) {
		var params: any = {}
		for (var key in parameters) {
			var value = parameters[key];
			if (value != null && value != '') {
				params[encodeURIComponent(key)] = encodeURIComponent(value);
			}
		}
		return params;
	}

	leftPad(val: string, size: number, ch: string): string {
		var result = String(val);
		if (!ch) {
			ch = " ";
		}
		while (result.length < size) {
			result = ch + result;
		}
		return result;
	}

	hexAcctNumber(num: number): string {
		//sign >>> is Zero-fill right shift
		return this.leftPad((num >>> 0).toString(16), 8, '0').toUpperCase();
	}

	isErrDuplicateEntry(err: Error): boolean {
		return err.message == 'duplicate entry';
	}

	isErrAccountLicenseExpired(err: Error): boolean {
		return err.message == 'account license expired';
	}

	isErrAccountLicenseInvalid(err: Error): boolean {
		return err.message == 'invalid account license';
	}

	getDefaultSaveMessage(): string {
		return "Successfully Saved";
	}

	oauthTokenApi(req: OAuthTokenRequest): Observable<OAuthToken> {
		return Observable.of(new OAuthToken());
	}

	containsUserRole(ur: UserRoles, role: string): boolean {
		for (var r in ur.roles) {
			if (ur.roles[r] == role) {
				return true;
			}
		}
		return false;
	}

	// Identity User API
	userRolesApi(): Observable<UserRoles[]> {
		var rows: UserRoles[] = [];
		rows.push({ acctNumber: 'A12F2343', schoolName: 'High School', isDefault: false, roles: ['admin', 'user'] });
		rows.push({ acctNumber: 'A12F2344', schoolName: 'College', isDefault: false, roles: ['admin', 'user'] });

		return Observable.create((observer: Observer<UserRoles[]>) => {
			observer.next(rows);
			observer.complete();
		});
	}

	updateUserApi(acctNumber: string, req: IdentityUser): Observable<boolean> {
		return Observable.create((observer: Observer<boolean>) => {
			observer.next(true);
			observer.complete();
		});
	}

	usersApi(acctNumber: string): Observable<IdentityUser[]> {
		let rows: IdentityUser[] = [];

		rows.push({
			roles: ['admin'],
			userId: '84c53f78-a50d-11ea-bb37-0242ac130002',
			firstName: 'Keanu',
			lastName: 'Reeves',
			email: 'keanu@test.com',
			openAddRole: false,
			status: 'test',
			owner: 'user',
		}, {
			roles: ['admin'],
			userId: '891dd260-a50d-11ea-bb37-0242ac130002',
			firstName: 'Johnny',
			lastName: 'Depp',
			email: 'johnny@email.com',
			openAddRole: false,
			status: 'test',
			owner: 'user',
		});

		return Observable.create((observer: Observer<IdentityUser[]>) => {
			observer.next(rows);
			observer.complete();
		});
	}

	inviteUserApi(acctNumber: string, req: InviteUser): Observable<IdentityUser> {
		let row: IdentityUser = new IdentityUser();

		row.roles = ['admin'];
		row.userId = 'id1';
		row.firstName = 'ben';
		row.lastName = 'lastname';
		row.email = req.email;
		row.openAddRole = false;
		row.status = 'invite';

		return Observable.create((observer: Observer<IdentityUser>) => {
			observer.next(row);
			observer.complete();
		});
	}

	removeUserApi(acctNumber: string, req: IdentityUser): Observable<boolean> {
		return Observable.create((observer: Observer<boolean>) => {
			observer.next(true);
			observer.complete();
		});
	}

	setDefaultApi(acctNumber: string): Observable<boolean> {
		return Observable.create((observer: Observer<boolean>) => {
			observer.next(true);
			observer.complete();
		});
	}

	// Idp Users API
	findIdPUserApi(acctNumber: string, req: FindIdPUser): Observable<IdPUser[]> {
		var rows: IdPUser[] = [];
		rows.push({
			id:0,
			idpId:1,
			username:'arianit@iakdev.aws',
			acctNumber: 'A12F2343',
			firstName:'Arianit',
			lastName:'Hetemi',
			cardId: '123456',
			groupNames:'school1, admin',
			phoneNumber:'012345',
			idpName: 'IAK AWS',
			userId: '100118b6-ab10-11ea-bb37-0242ac130002',
			createdAt: new Date(),
			updatedAt: new Date(),
			lastLoginAt: new Date(),
		},{
			id:1,
			idpId:2,
			username:'donjoe@iakdev.aws',
			acctNumber: 'A12F2344',
			firstName:'Joe',
			lastName:'Don',
			cardId: '123457',
			groupNames:'school1, admin',
			phoneNumber:'012345',
			idpName: 'IAK AWS 2',
			userId: '13a84ea8-ab10-11ea-bb37-0242ac130002',
			createdAt: new Date(),
			updatedAt: new Date(),
			lastLoginAt: new Date(),
		});

		return Observable.create((observer: Observer<IdPUser[]>) => {
			observer.next(rows);
			observer.complete();
		});
	}

	removeIdPUsersApi(acctNumber: string, id: number): Observable<IdPUserReply>{
		let row: IdPUserReply = { id: 1 };
		return Observable.of(row);
	}

	removeAllIdPUsersApi(acctNumber: string, req: IdPUserTextSearch): Observable<void>{
		return Observable.create((observer: Observer<IdPUserReply>) => {
			setTimeout(() => {
				observer.next(null);
				observer.complete();
			}, 1000);
		});
	}

	textSearchIdPUsersApi(acctNumber: string, req: IdPUserTextSearch): Observable<IdPUser[]>  {
		let rows: IdPUser[] = [];
		rows.push(
			{
				id:0,
				idpId:1,
				username:'arianit@identakid.com',
				acctNumber: 'A12F2343',
				firstName:'Arianit',
				lastName:'Hetemi',
				cardId: '123456',
				groupNames:'school1, admin',
				phoneNumber:'012345',
				idpName: 'IAK AWS',
				userId: '100118b6-ab10-11ea-bb37-0242ac130002',
				createdAt: new Date(),
				updatedAt: new Date(),
				lastLoginAt: new Date(),
			},{
				id:1,
				idpId:2,
				username:'donjoe@iakdev.aws',
				acctNumber: 'A12F2344',
				firstName:'Joe',
				lastName:'Don',
				cardId: '123457',
				groupNames:'school1, admin',
				phoneNumber:'012345',
				idpName: 'IAK AWS 2',
				userId: '13a84ea8-ab10-11ea-bb37-0242ac130002',
				createdAt: new Date(),
				updatedAt: new Date(),
				lastLoginAt: new Date(),
			});

		return Observable.create((observer: Observer<IdPUser[]>) => {
			observer.next(rows);
			observer.complete();
		});
	}

	textSearchCountIdPUsersApi(acctNumber: string, req: IdPUserTextSearch): Observable<IdPUserTextSearchCount>{
		let row: IdPUserTextSearchCount = {
			count: 2
		}
		return Observable.create((observer: Observer<IdPUserTextSearchCount>) => {
			observer.next(row);
			observer.complete();
		});
	}

	updateIdPUsersApi(acctNumber: string, id: number, req: IdPUserRequest): Observable<IdPUserReply> {
		let row: IdPUserReply = {
			id: 1,
		}
		return Observable.create((observer: Observer<IdPUserReply>) => {
			observer.next(row);
			observer.complete();
		});
	}

	addIdPUsersApi(acctNumber: string, req: IdPUserRequest): Observable<IdPUserReply>  {
		let rows: IdPUserReply = {
			id: 1,
		}
		return Observable.create((observer: Observer<IdPUserReply>) => {
			observer.next(rows);
			observer.complete();
		});
	}

	removeIdPUserRoleApi(acctNumber: string, id: number): Observable<IdPUserRoleReply>{
		let row: IdPUserRoleReply = { id: 1 };
		return Observable.of(row);
	}

	addIdPUserRoleApi(acctNumber: string,data: IdPUserRole): Observable<IdPUserRoleReply>{
		let rows: IdPUserRoleReply = {
			id: 1,
		}
		return Observable.create((observer: Observer<IdPUserReply>) => {
			observer.next(rows);
			observer.complete();
		});
	}

	updateIdPUserRolesApi(acctNumber: string, id: number,data: IdPUserRole): Observable<IdPUserRoleReply>{
		let rows: IdPUserRoleReply = {
			id: 1,
		}
		return Observable.create((observer: Observer<IdPUserReply>) => {
			observer.next(rows);
			observer.complete();
		});
	}

	findIdPUserRoleApi(acctNumber: string, req: FindIdPUser): Observable<IdPUserRole[]> {
		var rows: IdPUserRole[] = [];
		rows.push({
						id:0,
						idpId:0,
						userId:'',
						orgAcctNumber: -1471032103,
						serviceId:'school',
						roles:'admin',
						updatedAt: new Date(),
						organizationName:'Iak Dev1'
					});

		return Observable.create((observer: Observer<IdPUserRole[]>) => {
			observer.next(rows);
			observer.complete();
		});
	}

	// IdP APIs
	findIdPApi(acctNumber: string, req: FindIdP): Observable<IdP[]> {
		var rows: IdP[] = [];
		rows.push({
			id: 1,
			domainNames: 'iakdev.aws',
			name: 'IAK AWS',
			protocol: 'saml-2.0',
			userProvisioning: false,
			createdAt: new Date(),
			updatedAt: new Date(1,1,1)
		},{
			id: 2,
			domainNames: 'iakdev.aws',
			name: 'IAK AWS 2',
			protocol: 'saml-2.0',
			userProvisioning: false,
			createdAt: new Date(),
			updatedAt: new Date(1,1,1)
		})
		return Observable.create((observer: Observer<IdP[]>) => {
			observer.next(rows);
			observer.complete();
		});
	}

	removeIdPApi(acctNumber: string, id: number): Observable<IdPReply> {
		let row: IdPReply = { id: 1 };
		return Observable.of(row);
	}

	updateIdPApi(acctNumber: string, req: IdPRequest): Observable<IdPReply> {
		let row: IdPReply = { id: 1 };
		return Observable.create((observer: Observer<IdPReply>) => {
			observer.next(row);
			observer.complete();
		});
	}

	addIdPApi(acctNumber: string, req: IdPRequest): Observable<IdPReply> {
		let rows: IdPReply = {
			id: 1,
		}
		return Observable.create((observer: Observer<IdPReply>) => {
			observer.next(rows);
			observer.complete();
		});
	}

	saml2ConfigApi(acctNumber: string, req: FindIdP): Observable<SAML2Config> {
		let saml2cfg = new SAML2Config();
		saml2cfg.policy.manageUserProfile = true;

		saml2cfg.attributes = [
			{
				name: 'username',
				userAttributeMapping: 'username',
				attributeTransformationsRegex: '',
				attributeValidationsRegex: '',
				values: 'values ',
				skipIfBlank: true,
				defaultValues: 'some values',
				label: 'Email',
			},
			{
				name: 'FirstName',
				userAttributeMapping: 'first_name',
				attributeTransformationsRegex: '',
				attributeValidationsRegex: '',
				values: '',
				skipIfBlank: false,
				defaultValues: '',
				label: 'First Name',
			},
			{
				name: 'LastName',
				userAttributeMapping: 'last_name',
				attributeTransformationsRegex: '',
				attributeValidationsRegex: '',
				values: '',
				skipIfBlank: false,
				defaultValues: '',
				label: 'LastName',
			},
			{
				name: 'GroupNames',
				userAttributeMapping: 'group_names',
				attributeTransformationsRegex: '',
				attributeValidationsRegex: '',
				values: '',
				skipIfBlank: false,
				defaultValues: '',
				label: 'Group Names',
			},
			{
				name: 'CardId',
				userAttributeMapping: 'card_id',
				attributeTransformationsRegex: '',
				attributeValidationsRegex: '',
				values: '',
				skipIfBlank: false,
				defaultValues: '',
				label: 'Card ID',
			},
			// {
			// 	name: 'PhoneNumber',
			// 	userAttributeMapping: 'phone_number',
			// 	attributeTransformationsRegex: '',
			// 	attributeValidationsRegex: '',
			// 	values: '',
			// 	skipIfBlank: false,
			// 	defaultValues: '',
			// 	label: 'Phone Number',
			// },
		];

		return Observable.create((observer: Observer<SAML2Config>) => {
			observer.next(saml2cfg);
			observer.complete();
		});
	}

	saveSaml2ConfigApi(acctNumber: string, idpId: number, req: SAML2Config): Observable<IdPReply> {
		let row: IdPReply = { id: 1 };
		return Observable.create((observer: Observer<IdPReply>) => {
			observer.next(row);
			observer.complete();
		});
	}

	endpointsApi(acctNumber: string): Observable<EndpointsReply> {
		let row: EndpointsReply = {
			entityId: 'https://account.dev.identakid.net/api/saml2/metadata',
			metadataUrl: 'https://account.dev.identakid.net/api/saml2/metadata',
			loginUrl: 'https://account.dev.identakid.net/#/login?force_up=true',
			acsUrl: 'https://account.dev.identakid.net/api/saml2/acs',
		 };
		return Observable.create((observer: Observer<EndpointsReply>) => {
			observer.next(row);
			observer.complete();
		});
	}

	// Account Association
	findAccountAssociation(acctNumber: string, req: FindAccountAssociation): Observable<AccountAssociation[]> {
		var rows: AccountAssociation[] = [];
		rows.push({
				acctNumber: -1471032103,
				parentAccountNumber: 'parentacctnumber',
				organizationName: 'Iak Dev1',
				state: 'FL',
				zipCode: '3000',
				joinedAt: new Date('2018-08-20T20:24:41.103Z'),
				enabled: true,
				createdAt: new Date('2018-08-20T20:24:41.103Z'),
				updatedAt: new Date('2018-08-20T20:24:41.103Z'),
			}, {
				acctNumber: -1471032104,
				parentAccountNumber: 'parentacctnumber',
				organizationName: 'Iak Dev2',
				state: 'FL',
				zipCode: '3001',
				joinedAt: new Date('2018-08-20T20:24:41.103Z'),
				enabled: true,
				createdAt: new Date('2018-08-20T20:24:41.103Z'),
				updatedAt: new Date('2018-08-20T20:24:41.103Z'),
		});
		return Observable.create((observer: Observer<AccountAssociation[]>) => {
			observer.next(rows);
			observer.complete();
		});
	}

	findCountAccountAssociation(acctNumber: string, req: FindAccountAssociation): Observable<CountAccountAssociation> {
		let row: CountAccountAssociation = {
			count: 2
		}
		return Observable.create((observer: Observer<CountAccountAssociation>) => {
			observer.next(row);
			observer.complete();
		});
	}

	// IdP Domain
	findIdPDomainApi(acctNumber: string): Observable<IdPDomain[]> {
		var rows: IdPDomain[] = [];
		rows.push({
			id: 1,
			domainName: 'iakdev.aws',
			status: 'verified',
			createdAt: new Date()
		},{
			id: 2,
			domainName: 'domain.example',
			status: 'pending',
			createdAt: new Date()
		})
		return Observable.create((observer: Observer<IdPDomain[]>) => {
			observer.next(rows);
			observer.complete();
		});
	}

	removeIdPDomainApi(acctNumber: string, id: number): Observable<IdPDomainReply> {
		let row: IdPDomainReply = { id: 1 };
		return Observable.of(row);
	}

	resendDomainConfirmationApi(acctNumber: string, id: number): Observable<IdPDomainReply> {
		let row: IdPDomainReply = { id: 1 };
		return Observable.of(row);
	}

	addIdPDomainApi(acctNumber: string, req: IdPDomainRequest): Observable<IdPReply> {
		let rows: IdPDomainReply = {
			id: 1,
		}
		return Observable.create((observer: Observer<IdPDomainReply>) => {
			observer.next(rows);
			observer.complete();
		});
	}

	verifyIdPDomainApi(acctNumber: string, verifyToken: string): Observable<IdPDomainReply> {
		let rows: IdPDomainReply = {
			id: 1,
		}
		return Observable.create((observer: Observer<IdPDomainReply>) => {
			observer.next(rows);
			observer.complete();
		});
	}

	// Roles Mapping
	findIdPRolesMappingApi(acctNumber: string): Observable<IdPRolesMapping[]> {
		var rows: IdPRolesMapping[] = [];
		rows.push({
			id: 1,
			idpId: 1,
			accountGroups: 'school1,school2',
			orgAcctNumber: -1590746301,
			orgName: 'High School',
			serviceId: 'district',
			serviceRoles: 'admin,user,one-roster',
			serviceRolesGroups: 'administrator,staff',
			idpName: 'IAK AWS',
			createdAt: new Date(),
			updatedAt: new Date(),
		})
		return Observable.create((observer: Observer<IdPRolesMapping[]>) => {
			observer.next(rows);
			observer.complete();
		});
	}

	removeIdPRolesMappingApi(acctNumber: string, id: number): Observable<IdPRolesMappingReply>{
		let row: IdPRolesMappingReply = { id: 1 };
		return Observable.of(row);
	}

	updateIdPRolesMappingApi(acctNumber: string, req: IdPRolesMappingRequest): Observable<IdPRolesMappingReply> {
		let row: IdPRolesMappingReply = { id: 1 };
		return Observable.create((observer: Observer<IdPRolesMappingReply>) => {
			observer.next(row);
			observer.complete();
		});
	}

	addIdPRolesMappingApi(acctNumber: string, req: IdPRolesMappingRequest): Observable<IdPRolesMappingReply> {
		let rows: IdPRolesMappingReply = {
			id: 1,
		}
		return Observable.create((observer: Observer<IdPRolesMappingReply>) => {
			observer.next(rows);
			observer.complete();
		});
	}

	textSearchIdPRolesMappingApi(acctNumber: string, req: IdPRolesMappingTextSearch): Observable<IdPRolesMapping[]> {
		var rows: IdPRolesMapping[] = [];
		rows.push({
			id: 1,
			idpId: 1,
			accountGroups: 'school1,school2',
			orgAcctNumber: -1590746301,
			orgName: 'High School',
			serviceId: 'district',
			serviceRoles: 'admin,user,one-roster',
			serviceRolesGroups: 'administrator,staff',
			idpName: 'IAK AWS',
			createdAt: new Date(),
			updatedAt: new Date(),
		})
		return Observable.create((observer: Observer<IdPRolesMapping[]>) => {
			observer.next(rows);
			observer.complete();
		});
	}

	textSearchCountIdPRolesMappingApi(acctNumber: string, req: IdPRolesMappingTextSearch): Observable<IdPRolesMappingTextSearchCount> {
		let row: IdPRolesMappingTextSearchCount = {
			count: 2
		}
		return Observable.create((observer: Observer<IdPRolesMappingTextSearchCount>) => {
			observer.next(row);
			observer.complete();
		});
	}
}
